/**
 * Blockly Message Loader
 * 
 * This utility ensures that Blockly's message strings, particularly for HUE values,
 * are available. In some build environments (like Vite), these can be missing,
 * causing blocks to render with placeholder text like `%{BKY_LOGIC_HUE}`.
 * 
 * This script safely loads the default English messages and merges custom fallbacks
 * to ensure all required message strings are present.
 */

// A minimal set of fallback HUE values, in case they are missing from the default set.
const fallbackHue: Record<string, string> = {
  'LOGIC_HUE': '210',
  'LOOPS_HUE': '120',
  'MATH_HUE': '230',
  'TEXTS_HUE': '160',
  'LISTS_HUE': '260',
  'COLOUR_HUE': '20',
  'VARIABLES_HUE': '330',
  'VARIABLES_DYNAMIC_HUE': '310',
  'PROCEDURES_HUE': '290',
};

/**
 * Initializes Blockly messages, providing fallbacks for missing HUE values.
 * This should be called once when the application loads.
 */
export async function initializeBlocklyMessages(): Promise<void> {
  try {
    // Dynamically import Blockly and messages to avoid static import issues
    const [blocklyModule, enModule] = await Promise.all([
      import('blockly'),
      import('blockly/msg/en')
    ]);

    const Blockly = blocklyModule;
    const en = enModule;

    // When using `import * as en`, the module might have a `default` export
    // that is not a string, which causes issues with `setLocale`.
    // We destructure it to separate the messages from the default export,
    // ignoring the default export as it's not needed.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { default: _default, ...messages } = en;

    // Combine the official 'en' messages with our custom fallback HUEs.
    // The fallbacks will only be used if a key is not present in the 'en' object.
    const combinedMessages = { ...fallbackHue, ...messages };
    Blockly.setLocale(combinedMessages);
    
    // The setLocale function handles everything. We can keep the debug log
    // to confirm that the messages are loaded as expected.
    console.log('✅ [MessageLoader] Blockly messages initialized using setLocale.');
  } catch (error) {
    console.warn('⚠️ [MessageLoader] Failed to initialize Blockly messages:', error);
    // Continue without Blockly messages - the app should still work
  }
}

export async function debugBlocklyMessages() {
  try {
    const blocklyModule = await import('blockly');
    const Blockly = blocklyModule;
    
    console.log('🔍 [MessageLoader] Blockly.Msg debug info:');
    console.log('  - Total keys:', Object.keys(Blockly.Msg || {}).length);
    console.log('  - HUE values:', {
      LOGIC_HUE: Blockly.Msg?.LOGIC_HUE,
      MATH_HUE: Blockly.Msg?.MATH_HUE,
      TEXT_HUE: Blockly.Msg?.TEXT_HUE,
      LOOPS_HUE: Blockly.Msg?.LOOPS_HUE
    });
    console.log('  - Sample keys:', Object.keys(Blockly.Msg || {}).slice(0, 10));
  } catch (error) {
    console.warn('⚠️ [MessageLoader] Failed to debug Blockly messages:', error);
  }
} 